import { CountryUtils } from '@spotify/confidence-core-react';

import { Criterion, CriterionInfo, TargetingValue } from './types';

export const hasChanged = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) !== JSON.stringify(b);

export const formatAllocation = (
  value: number | string | undefined | null,
): string => {
  if (value === undefined || value === null) return 'Not set';
  return `${Math.round(Number(value) * 100)}%`;
};

const formatTimestamp = (ts: string): string => {
  const date = new Date(ts);
  if (isNaN(date.getTime())) return ts;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isCountryAttribute = (attributeName: string | undefined): boolean =>
  attributeName === 'registration_country' || attributeName === 'country';

const formatValue = (
  value: TargetingValue | null | undefined,
  isCountry?: boolean,
): string => {
  if (!value) return '';
  if (value.stringValue !== undefined && value.stringValue !== null) {
    return isCountry
      ? CountryUtils.getCountryLabel(value.stringValue)
      : value.stringValue;
  }
  if (value.numberValue !== undefined && value.numberValue !== null)
    return String(value.numberValue);
  if (value.boolValue !== undefined && value.boolValue !== null)
    return String(value.boolValue);
  if (value.timestampValue !== undefined && value.timestampValue !== null)
    return formatTimestamp(value.timestampValue);
  if (
    value.versionValue?.version !== undefined &&
    value.versionValue?.version !== null
  )
    return value.versionValue.version;
  return '';
};

const formatRuleValue = (attr: NonNullable<Criterion['attribute']>): string => {
  const country = isCountryAttribute(attr.attributeName);

  if (attr.eqRule?.value) {
    const v = formatValue(attr.eqRule.value, country);
    return v ? `is ${v}` : '';
  }

  if (attr.setRule?.values) {
    const values = attr.setRule.values
      .map(v => formatValue(v, country))
      .filter(Boolean);
    return values.length > 0 ? `in [${values.join(', ')}]` : '';
  }

  if (attr.rangeRule) {
    const parts: string[] = [];
    const r = attr.rangeRule;
    if (r.startInclusive) parts.push(`>= ${formatValue(r.startInclusive)}`);
    if (r.startExclusive) parts.push(`> ${formatValue(r.startExclusive)}`);
    if (r.endInclusive) parts.push(`<= ${formatValue(r.endInclusive)}`);
    if (r.endExclusive) parts.push(`< ${formatValue(r.endExclusive)}`);
    return parts.join(' and ');
  }

  if (attr.startsWithRule?.value) {
    return `starts with ${attr.startsWithRule.value}`;
  }

  if (attr.endsWithRule?.value) {
    return `ends with ${attr.endsWithRule.value}`;
  }

  return '';
};

const formatAttributeName = (attributeName: string): string => {
  return attributeName.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
};

const isHoldback = (criterion: Criterion): boolean =>
  criterion.segment?.labels?.some(
    l => l.key === 'type' && l.value === 'holdback',
  ) ?? false;

export const describeCriterion = (criterion: Criterion): CriterionInfo => {
  if (criterion.attribute) {
    const name =
      criterion.attribute.displayName ??
      (criterion.attribute.attributeName
        ? formatAttributeName(criterion.attribute.attributeName)
        : 'unknown');
    const rule = formatRuleValue(criterion.attribute);
    return { tagLabel: name, value: rule };
  }

  if (criterion.segment) {
    const name =
      criterion.segment.displayName ??
      criterion.segment.segment?.split('/').pop() ??
      'unknown';
    const label = isHoldback(criterion) ? 'Holdback' : 'Segment';
    return { tagLabel: label, value: name };
  }

  if (criterion.materializedSegment) {
    const name =
      criterion.materializedSegment.displayName ??
      criterion.materializedSegment.materializedSegment?.split('/').pop() ??
      'unknown';
    return { tagLabel: 'Materialized segment', value: name };
  }
  return { tagLabel: 'Unknown', value: '' };
};
